// Low-level WebGL2 helpers shared by every scene.

export const VS_FULLSCREEN = `#version 300 es
const vec2 P[3]=vec2[3](vec2(-1,-1),vec2(3,-1),vec2(-1,3));
out vec2 vUV;
void main(){vec2 p=P[gl_VertexID];vUV=p*.5+.5;gl_Position=vec4(p,0,1);}`;

/** Common fragment header: uniforms every scene receives, plus noise helpers. */
export const HDR = `#version 300 es
precision highp float;
uniform vec2 R; uniform float T,K,BT,KP,Q;
out vec4 O;
#define PI 3.14159265
mat2 rot(float a){float c=cos(a),s=sin(a);return mat2(c,-s,s,c);}
float h11(float x){return fract(sin(x*127.1)*43758.5453);}
float h21(vec2 p){p=fract(p*vec2(123.34,456.21));p+=dot(p,p+45.32);return fract(p.x*p.y);}
float vnoise(vec2 p){vec2 i=floor(p),f=fract(p);f=f*f*(3.-2.*f);return mix(mix(h21(i),h21(i+vec2(1,0)),f.x),mix(h21(i+vec2(0,1)),h21(i+1.),f.x),f.y);}
float fbm(vec2 p){float v=0.,a=.5;for(int i=0;i<5;i++){v+=a*vnoise(p);p=p*2.03+vec2(1.7,9.2);a*=.5;}return v;}
vec3 pal(float t){return .5+.5*cos(6.2831*(t+vec3(0.,.33,.67)));}
`;

export interface Program { p: WebGLProgram; u: Record<string, WebGLUniformLocation> }

export function compile(gl: WebGL2RenderingContext, type: number, src: string): WebGLShader {
  const s = gl.createShader(type)!;
  gl.shaderSource(s, src);
  gl.compileShader(s);
  if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
    const log = gl.getShaderInfoLog(s) || 'shader error';
    console.error(log + '\n' + src.split('\n').map((l, i) => `${i + 1}: ${l}`).join('\n'));
    throw new Error(log);
  }
  return s;
}

export function program(gl: WebGL2RenderingContext, vs: string, fs: string): Program {
  const p = gl.createProgram()!;
  gl.attachShader(p, compile(gl, gl.VERTEX_SHADER, vs));
  gl.attachShader(p, compile(gl, gl.FRAGMENT_SHADER, fs));
  gl.linkProgram(p);
  if (!gl.getProgramParameter(p, gl.LINK_STATUS)) throw new Error(gl.getProgramInfoLog(p) || 'link error');
  const u: Record<string, WebGLUniformLocation> = {};
  const n = gl.getProgramParameter(p, gl.ACTIVE_UNIFORMS) as number;
  for (let i = 0; i < n; i++) {
    const info = gl.getActiveUniform(p, i)!;
    const loc = gl.getUniformLocation(p, info.name);
    if (loc) u[info.name.replace('[0]', '')] = loc;
  }
  return { p, u };
}

export function deleteProgram(gl: WebGL2RenderingContext, pr: Program) {
  gl.getAttachedShaders(pr.p)?.forEach((s) => { gl.detachShader(pr.p, s); gl.deleteShader(s); });
  gl.deleteProgram(pr.p);
}

/** Tiny typed uniform setter that ignores uniforms the compiler optimised away. */
export class U {
  constructor(private gl: WebGL2RenderingContext, private pr: Program) {}
  f(n: string, v: number) { const l = this.pr.u[n]; if (l) this.gl.uniform1f(l, v); return this; }
  v2(n: string, a: number, b: number) { const l = this.pr.u[n]; if (l) this.gl.uniform2f(l, a, b); return this; }
  v3(n: string, v: ArrayLike<number>) { const l = this.pr.u[n]; if (l) this.gl.uniform3f(l, v[0], v[1], v[2]); return this; }
  m4(n: string, m: Float32Array) { const l = this.pr.u[n]; if (l) this.gl.uniformMatrix4fv(l, false, m); return this; }
  tex(n: string, unit: number, t: WebGLTexture) {
    this.gl.activeTexture(this.gl.TEXTURE0 + unit); this.gl.bindTexture(this.gl.TEXTURE_2D, t);
    const l = this.pr.u[n]; if (l) this.gl.uniform1i(l, unit); return this;
  }
}

export function makeTexture(gl: WebGL2RenderingContext, w: number, h: number, src?: TexImageSource, filter: number = gl.LINEAR): WebGLTexture {
  const t = gl.createTexture()!;
  gl.bindTexture(gl.TEXTURE_2D, t);
  if (src) gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, src);
  else gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA8, w, h, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, filter);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, filter);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  return t;
}

export interface Fbo { f: WebGLFramebuffer; t: WebGLTexture; d?: WebGLRenderbuffer; w: number; h: number }

export function makeFbo(gl: WebGL2RenderingContext, w: number, h: number, depth: boolean): Fbo {
  const t = makeTexture(gl, w, h);
  const f = gl.createFramebuffer()!;
  gl.bindFramebuffer(gl.FRAMEBUFFER, f);
  gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, t, 0);
  let d: WebGLRenderbuffer | undefined;
  if (depth) {
    d = gl.createRenderbuffer()!;
    gl.bindRenderbuffer(gl.RENDERBUFFER, d);
    gl.renderbufferStorage(gl.RENDERBUFFER, gl.DEPTH_COMPONENT24, w, h);
    gl.framebufferRenderbuffer(gl.FRAMEBUFFER, gl.DEPTH_ATTACHMENT, gl.RENDERBUFFER, d);
  }
  return { f, t, d, w, h };
}

export function deleteFbo(gl: WebGL2RenderingContext, f: Fbo) {
  gl.deleteFramebuffer(f.f); gl.deleteTexture(f.t); if (f.d) gl.deleteRenderbuffer(f.d);
}

// ------------------------------------------------------------------ 3D math
export type V3 = [number, number, number];
export const sub = (a: V3, b: V3): V3 => [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
export const dot = (a: V3, b: V3) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
export const cross = (a: V3, b: V3): V3 => [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
export const norm = (a: V3): V3 => { const l = Math.hypot(a[0], a[1], a[2]) || 1; return [a[0] / l, a[1] / l, a[2] / l]; };

export function perspective(fovy: number, aspect: number, n: number, f: number) {
  const t = 1 / Math.tan(fovy / 2);
  return new Float32Array([t / aspect, 0, 0, 0, 0, t, 0, 0, 0, 0, (f + n) / (n - f), -1, 0, 0, 2 * f * n / (n - f), 0]);
}
export function lookAt(e: V3, c: V3, up: V3 = [0, 1, 0]) {
  const z = norm(sub(e, c)), x = norm(cross(up, z)), y = cross(z, x);
  return new Float32Array([x[0], y[0], z[0], 0, x[1], y[1], z[1], 0, x[2], y[2], z[2], 0, -dot(x, e), -dot(y, e), -dot(z, e), 1]);
}
export function mmul(a: Float32Array, b: Float32Array) {
  const r = new Float32Array(16);
  for (let c = 0; c < 4; c++) for (let i = 0; i < 4; i++) { let s = 0; for (let k = 0; k < 4; k++) s += a[k * 4 + i] * b[c * 4 + k]; r[c * 4 + i] = s; }
  return r;
}

export const clamp = (x: number, a: number, b: number) => Math.min(b, Math.max(a, x));
export const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
export const smooth = (t: number) => t * t * (3 - 2 * t);
export function rng(seed: number) {
  return () => { seed = (seed + 0x6d2b79f5) | 0; let t = Math.imul(seed ^ (seed >>> 15), 1 | seed); t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t; return ((t ^ (t >>> 14)) >>> 0) / 4294967296; };
}
